import * as React from 'react';
import { DataGrid, GridActionsCellItem } from '@mui/x-data-grid';
import api from '../../../services/api';
import {
  Paper,
  Button,
  Box,
  Typography,
  Container,
  FormControl,
  Stack,
  Divider
} from '@mui/material';
import Grid from "@mui/material/Grid2";
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { useStoreActions, useStoreState } from 'easy-peasy';
import {
  Autocomplete,
  Datas,
  NoRowsOverlay,
  AprovacoesCheckbox
} from '../../../components';
import DialogoProducao from './DialogoProducao';
import DadosGerais from './DadosGerais';
import DeleteIcon from '@mui/icons-material/Delete';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import { CSVDownload } from 'react-csv';
import { tagsRel, tagsRelProd } from './static';
import { removerQuebrasDeLinha } from '../../../utils/removerQuebraDeLinha';

const Tema00 = () => {
  let startLoading = useStoreActions(actions => actions.nav.startLoading);
  let stopLoading = useStoreActions(actions => actions.nav.stopLoading);
  let user = useStoreState(state => state.auth.user);

  const [rows, setRows] = React.useState([]);
  const [openDlgProducao, setOpenDlgProducao] = React.useState(false);
  const [empresas, setEmpresas] = React.useState([]);
  const [ano, setAno] = React.useState(new Date().getFullYear() - 1);
  const [selectedProducao, setSelectedProducao] = React.useState('');
  const [csvData, setCsvData] = React.useState([]);
  const [csvDataProd, setCsvDataProd] = React.useState([]);

  const schema = yup
    .object({
      empresaId: yup.number().nullable().label('empresas').required()
    })
    .required();

  const useFormRef = useForm({
    mode: 'onTouched',
    resolver: yupResolver(schema)
  });

  const {
    handleSubmit,
    reset,
    watch,
    formState: { isDirty, isSubmitting }
  } = useFormRef;

  const watchEmpresaId = watch('empresaId');

  const novoRegistro = async () => {
    setOpenDlgProducao(true);
  };

  const onCloseDlgProducao = (reload = false) => {
    setSelectedProducao(null);
    setOpenDlgProducao(false);
    if (reload) {
      loadProducao();
    }
  };

  const loadProducao = async (mount = null) => {
    startLoading();
    try {
      let getRows = (
        await api.http.get(`/00/producao/empresa/${watchEmpresaId}/${ano}`)
      ).data;
      if (!mount || mount.isMounted) setRows(getRows);
      stopLoading();
    } catch (error) {
      stopLoading({
        message: error.toString(),
        severity: 'error'
      });
    }
  };

  React.useEffect(() => {
    let mount = {
      isMounted: true
    };
    if (watchEmpresaId) loadProducao(mount);
    return () => {
      mount.isMounted = false;
    };
  }, [ano, watchEmpresaId]);

  const loadEmpresas = async mount => {
    startLoading();
    try {
      let data = (
        await api.http.get(
          '/empresas/' +
            (user.roleId == 'admin' ? '?all=1&matriz=1' : '?matriz=1')
        )
      ).data;
      if (mount.isMounted) setEmpresas(data);
      stopLoading();
    } catch (error) {
      stopLoading({
        message: error.toString(),
        severity: 'error'
      });
      throw error;
    }
  };

  React.useEffect(() => {
    let mount = {
      isMounted: true
    };
    loadEmpresas(mount);
    return () => {
      mount.isMounted = false;
    };
  }, []);

  function onChangeAno(event, ano) {
    setAno(ano);
  }
  const onRowClick = (params, event, details) => {
    setSelectedProducao(params.row);
    setOpenDlgProducao(true);
  };

  const deleteDados = row => async () => {
    startLoading();
    try {
      await api.http.delete(`/00/producao/empresa/${row.empresaId}/${row.id}`);
      loadProducao();
      stopLoading({ severity: 'success' });
    } catch (error) {
      stopLoading({
        message: error.toString(),
        severity: 'error'
      });
    }
  };

  const columns = React.useMemo(
    () => [
      {
        field: 'produto',
        headerName: 'Produto',
        flex: 1,
        valueGetter: (value) => {
          return value.name}
      },
      {
        field: 'producaoBruta',
        flex: 1,
        headerName: 'ROM(ton)',
        editable: true
      },
      {
        field: 'producaoConcentrado',
        headerName: 'Concentrado(ton)',
        flex: 1
      },
      {
        field: 'unidades',
        headerName: 'Plantas',
        flex: 1
      },
      {
        field: 'actions',
        type: 'actions',
        headerName: 'Remover',
        width: 100,
        getActions: params => [
          <GridActionsCellItem
            icon={<DeleteIcon />}
            label="Delete"
            onClick={deleteDados(params.row)}
          />
        ]
      }
    ],
    [deleteDados]
  );

  function extrairDataHora(dateTimeStr) {
    const dateObj = new Date(dateTimeStr);
    const year = dateObj.getFullYear().toString();
    const month = (dateObj.getMonth() + 1).toString().padStart(2, '0');
    const day = dateObj.getDate().toString().padStart(2, '0');
    const date = `${day}-${month}-${year}`;
    const hours = dateObj.getHours().toString().padStart(2, '0');
    const minutes = dateObj.getMinutes().toString().padStart(2, '0');
    const seconds = dateObj.getSeconds().toString().padStart(2, '0');
    const time = `${hours}:${minutes}:${seconds}`;
    return date + ' ' + time;
  }
  function converterParaReal(valor) {
    const valorFormatado = valor.toLocaleString('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    });
    return valorFormatado;
  }
  function converterParaDolar(valor) {
    const valorFormatado = valor.toLocaleString('en-US', {
      style: 'currency',
      currency: 'USD'
    });
    return valorFormatado;
  }

  const exportDadosGerais = async () => {
    startLoading();
    try {
      const dados = (await api.http.get('/00/dados-gerais')).data;
      const dadosCsv = dados.map((data, index) => {
        return {
          cnpj: data?.empresa?.cnpj,
          empresa: removerQuebrasDeLinha(data.empresa.nome),
          ano: data.ano,
          faturamentoBrutoDolar: data.faturamentoBrutoDolar
            ? converterParaDolar(data.faturamentoBrutoDolar)
            : null,
          faturamentoBrutoReal: data.faturamentoBrutoReal
            ? converterParaReal(data.faturamentoBrutoReal)
            : null,
          capitalSocial: data.capitalSocial
            ? converterParaReal(data.capitalSocial)
            : null,
          observacoes: data.observacoes
            ? removerQuebrasDeLinha(data.observacoes)
            : null,
          createdAt: data.createdAt ? extrairDataHora(data.createdAt) : null,
          updatedAt: data.updatedAt ? extrairDataHora(data.updatedAt) : null
        };
      });

      if (dadosCsv.length == 0) {
        stopLoading({
          message: 'Nenhum registro encontrado.',
          severity: 'error'
        });
      } else {
        setCsvData([tagsRel, ...dadosCsv]);
        stopLoading();
      }
    } catch (error) {
      stopLoading({
        message: error.toString(),
        severity: 'error'
      });
    }
  };
  const exportDadosProducao = async () => {
    startLoading();
    try {
      const dadosProducao = (await api.http.get('/00/producao')).data;
      const dadosCsvProducao = dadosProducao.map(data => {
        return {
          cnpj: data?.empresa?.cnpj,
          empresa: removerQuebrasDeLinha(data?.empresa?.nome),
          ano: data?.ano,
          produto: removerQuebrasDeLinha(data?.produto?.name),
          producaoBruta: data?.producaoBruta,
          producaoConcentrado: data?.producaoConcentrado,
          unidades: removerQuebrasDeLinha(data?.unidades),
          producaoCreatedAt: data.createdAt
            ? extrairDataHora(data.createdAt)
            : null,
          producaoUpdatedAt: data.updatedAt
            ? extrairDataHora(data.updatedAt)
            : null
        };
      });

      if (dadosCsvProducao.length == 0) {
        stopLoading({
          message: 'Nenhum registro encontrado.',
          severity: 'error'
        });
      } else {
        setCsvDataProd([tagsRelProd, ...dadosCsvProducao]);
        stopLoading();
      }
    } catch (error) {
      stopLoading({
        message: error.toString(),
        severity: 'error'
      });
    }
  };

  return (
    (<Container maxWidth="false">
      <Paper
        variant="outlined"
        className="center-box"
        sx={{
          textAlign: 'center',
          px: 4,
          pt: 2,
          pb: 3,
          m: 5
        }}
      >
        <Divider>
          <h3>Dados básicos</h3>
        </Divider>
        <Box component="form" autoComplete="off">
          <Stack direction="row" spacing={2}>
            <FormControl fullWidth>
              <Autocomplete
                field="empresaId"
                useFormRef={useFormRef}
                options={empresas}
                getOptionLabel={empresa =>
                  empresa.nome + ' (' + empresa.cnpj + ')'
                }
                schema={schema}
              />
            </FormControl>
            <Datas onChange={onChangeAno} value={ano} />
          </Stack>
          <Stack direction="row" spacing={2}>
            <AprovacoesCheckbox />
          </Stack>
          {['admin', 'gestor', 'preenchedorSso'].includes(user.roleId) && (
            <Stack direction="row" spacing={2} mt={2}>
              <Button
                variant="contained"
                onClick={exportDadosGerais}
                sx={{ height: 45 }}
              >
                <FileDownloadIcon /> Exportar Dados Gerais (CSV)
              </Button>
              {csvData && csvData.length > 0 && (
                <CSVDownload
                  data={csvData}
                  headers={tagsRel.headers}
                  separator=";"
                  target="_blank"
                />
              )}

              <Button
                variant="contained"
                onClick={exportDadosProducao}
                sx={{ height: 45 }}
              >
                <FileDownloadIcon /> Exportar Dados Produção (CSV)
              </Button>
              {csvDataProd && csvDataProd.length > 0 && (
                <CSVDownload
                  data={csvDataProd}
                  headers={tagsRelProd.headers}
                  separator=";"
                  target="_blank"
                />
              )}
            </Stack>
          )}
        </Box>
      </Paper>
      {watchEmpresaId > 0 && (
        <div>
          <Paper
            variant="outlined"
            className="preenchimento-grid center-box"
            sx={{
              textAlign: 'center',
              px: 4,
              pt: 2,
              pb: 3,
              m: 5
            }}
          >
            <DadosGerais ano={ano} watchEmpresaId={watchEmpresaId} />
          </Paper>
          <DialogoProducao
            watchEmpresaId={watchEmpresaId}
            ano={ano}
            selectedProducao={selectedProducao}
            open={openDlgProducao}
            onClose={onCloseDlgProducao}
          />

          <Grid container spacing={3} sx={{ my: 2 }}>
            <Grid style={{ textAlign: 'left' }} size="grow">
              <Typography variant="h4">Produção</Typography>
            </Grid>
            <Grid style={{ textAlign: 'right' }} size="grow">
              <Button color="primary" variant="outlined" onClick={novoRegistro}>
                Novo Registro de Produção
              </Button>
            </Grid>
          </Grid>
          <Paper
            variant="elevation"
            elevation={2}
            sx={{
              mb: 5
            }}
            style={{
              height: 400,
              width: '100%'
            }}
          >
            <DataGrid
              rows={rows}
              columns={columns}
              pageSize={5}
              rowsPerPageOptions={[5]}
              onRowClick={onRowClick}
              slots={{ noRowsOverlay: NoRowsOverlay }}
              disableSelectionOnClick
            />
          </Paper>
        </div>
      )}
    </Container>)
  );
};

export default Tema00;
